import type { FieldGroup, FormField } from '@/types/formSchema';

/**
 * Per-parent child-index map used to sort engine field paths by Nomos UI
 * schema order.
 *
 * Key: normalized parent path with array indices stripped (e.g.
 * `"drug.coverage"`). Root uses the empty string `""`, where top-level
 * fields are flattened across groups in group order — so group-0 fields
 * occupy indices 0..n, then group-1 fields continue from n+1.
 *
 * Value: map from child field `id` → its position within that parent's
 * `fields[]` (Nomos canonical order, since the backend already sorts by
 * `FormField.Order` and the wire response preserves array position).
 */
export type FieldSchemaOrderMap = Map<string, Map<string, number>>;

type FieldLike = Pick<FormField, 'id' | 'type' | 'fields'>;

/**
 * Walks the parsed output schema and builds a {@link FieldSchemaOrderMap}.
 *
 * Top-level fields across groups are flattened into the root parent (`""`)
 * with a running counter so group order is baked into the root entry — no
 * separate group-index tracking is needed when sorting.
 */
export function buildFieldSchemaOrder(groups: FieldGroup[] | undefined): FieldSchemaOrderMap {
  const order: FieldSchemaOrderMap = new Map();
  if (!groups || groups.length === 0) return order;

  const rootChildren = new Map<string, number>();
  order.set('', rootChildren);
  let rootCounter = 0;

  const walkChildren = (parentPath: string, fields: FieldLike[] | undefined) => {
    if (!fields || fields.length === 0) return;
    const children = new Map<string, number>();
    fields.forEach((field, index) => {
      if (!field.id) return;
      children.set(field.id, index);
      if (field.fields && field.fields.length > 0) {
        const childPath = parentPath ? `${parentPath}.${field.id}` : field.id;
        walkChildren(childPath, field.fields);
      }
    });
    order.set(parentPath, children);
  };

  groups.forEach(group => {
    group.fields.forEach(field => {
      if (!field.id) return;
      rootChildren.set(field.id, rootCounter++);
      if (field.fields && field.fields.length > 0) {
        walkChildren(field.id, field.fields);
      }
    });
  });

  return order;
}

const SEGMENT_RE = /^([^[\]]+)(?:\[(\d+)\])?$/;
const UNKNOWN = Number.MAX_SAFE_INTEGER;

/**
 * Builds a sort key for a single engine field path so chips can be ordered
 * by Nomos schema position with array items grouped together.
 *
 * For each path segment we append the segment's position within its parent
 * (from {@link FieldSchemaOrderMap}) and — when the segment carries an
 * array index `[N]` — the index itself. The interleaving causes runtime
 * array items to cluster: all of `procedure[0]`'s nested fields sort
 * before any of `procedure[1]`'s.
 *
 * Unknown segments fall through to {@link Number.MAX_SAFE_INTEGER} so
 * unrecognized paths sink to the end without throwing.
 *
 * @example
 *   // procedure(pos 1) > item[0] > coverage(pos 0) > oopmax_applies(pos 2)
 *   getAskNextSortKey('procedure[0].coverage.oopmax_applies', orderMap)
 *     // → [1, 0, 0, 2]
 */
export function getAskNextSortKey(fieldPath: string, orderMap: FieldSchemaOrderMap): number[] {
  const key: number[] = [];
  let parentPath = '';
  const segments = fieldPath.split('.');
  for (const segment of segments) {
    const match = SEGMENT_RE.exec(segment);
    if (!match) {
      key.push(UNKNOWN);
      continue;
    }
    const name = match[1];
    const arrayIdx = match[2] === undefined ? undefined : Number(match[2]);
    const position = orderMap.get(parentPath)?.get(name) ?? UNKNOWN;
    key.push(position);
    if (arrayIdx !== undefined) key.push(arrayIdx);
    parentPath = parentPath ? `${parentPath}.${name}` : name;
  }
  return key;
}

/** Lexicographic comparison of two numeric sort keys produced by {@link getAskNextSortKey}. */
export function compareSortKeys(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return a.length - b.length;
}
